import { Box, Button, Modal, Stack, TextField, Typography } from "@mui/material"
import Uppy from "@uppy/core";
import { Dashboard } from "@uppy/react";
import { useGetProjectFloorsQuery } from "api/project/queries/useGetProjectFloors";
import { useAtom } from "jotai"
import { useState } from "react";
import { useParams } from "react-router-dom";
import { newFloorModalOpenAtom } from "store/atoms/floorplanAlignment/layersAtom"
import AwsS3 from '@uppy/aws-s3';
import CONFIG from "utils/config";
import useAddFloorMutation from "api/project/mutations/useAddFloorMutation";
import useUpdateFloorImageMutation from "api/project/mutations/useUpdateFloorImageMutation";
import { toast } from "react-toastify";
import { useGetProjectQuery } from "api/project/queries/useGetProjectQuery";
import { Floor } from "utils/floor";

const NEW_FLOORPLAN_SPACING_IN_Z = 1;

const AddFloorModal = () => {

  const [open, setOpen] = useAtom(newFloorModalOpenAtom);
  const [selectedFile, setSelectedFile] = useState<{
    s3Name: string;
    uploaded: boolean;
    dimensions: {
      width: number;
      length: number;
    }
  } | undefined>();
    const { projectSlug, workspaceSlug } = useParams();
    const { data: project } = useGetProjectQuery(projectSlug || "", workspaceSlug || "");
  const { data: floors } = useGetProjectFloorsQuery(project?.id || "");
  
  // input field states
  const [floorName, setFloorName] = useState("");
  const [mpp, setMpp] = useState<number | undefined>();
  const [height, setHeight] = useState<number | undefined>();
  const [isUploading, setIsUploading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createdFloorId, setCreatedFloorId] = useState<string | null>(null);
  
  const { mutate: addFloor } = useAddFloorMutation(project?.id || "");
  const { mutate: updateFloorImage, isPending: isUpdatingFloorImage } = useUpdateFloorImageMutation(project?.id || "");
  const [uppy] = useState(() => new Uppy().use(AwsS3, {
    endpoint: `${CONFIG.TRANSFERS_API_URL}/companion`,
    shouldUseMultipart: true,
    
  }));
  uppy.setOptions({
    restrictions: {
      allowedFileTypes: ['image/*'],
      maxNumberOfFiles: 1,
    }
  })
  uppy.on("upload-start", () => {
    setIsUploading(true);
  });
  uppy.on("complete", (result) => {
    setIsUploading(false);
    const { successful } = result;
    if (!successful || successful.length === 0) return;
    
    setSelectedFile((prev) => prev && {
      ...prev,
      uploaded: true
    });
    
    // Update floor with correct s3 path after successful upload
    if (createdFloorId && selectedFile) {
      const fileExtension = selectedFile.s3Name.split('.').pop() || 'png';
      const fileName = `${createdFloorId}.${fileExtension}`;
      console.log("Updating floor image with", fileName);
      
      updateFloorImage({
        floorId: createdFloorId,
        payload: {
          s3Url: fileName,
          metadata: {
            floorplan: {
              width: selectedFile.dimensions.width,
              height: selectedFile.dimensions.length
            },
            mpp: mpp
          }
        }
      }, {
        onSuccess: () => {
          toast.success("Floor created and image uploaded successfully");
          onCloseModal();
        },
        onError: () => {
          toast.error("Failed to update floor image");
        }
      });
    }
  });

  uppy.on("file-added", (file) => {
    file.name = file.name ? file.name.replace(/\s+/g, "-") : file.id;
    // Will be updated with floor ID after floor creation
    file.meta.s3Key = `projects/${project?.id}/floorplans/${file.name}`;
    const newImage = new Image();
    newImage.src = file.preview || URL.createObjectURL(file.data);
    newImage.onload = () => {
      setSelectedFile({
        s3Name: file.name || file.id,
        uploaded: false,
        dimensions: {
          width: newImage.width,
          length: newImage.height
        }
      });
    }
  })
  
  const createDisabled = Boolean(!selectedFile || !floorName || isCreating || floors?.some((floor) => floor.name === floorName) || isUpdatingFloorImage || isUploading);

  const onCloseModal = () => {
    setSelectedFile(undefined);
    setFloorName("");
    setMpp(undefined);
    setHeight(undefined);
    setIsCreating(false);
    setCreatedFloorId(null);
    uppy.clear();
    setOpen(false);
  }

  const createFloor = () => {
    if (!selectedFile || !floorName) return;

    setIsCreating(true);

    let maxHeight = floors?.[0]?.position.z;

    for (const floor of (floors || [])) {
      if (floor.name === floorName) {
        toast.error("Floor name already exists");
        setIsCreating(false);
        return;
      }

      maxHeight = maxHeight === undefined ? 0 : Math.max(maxHeight, floor.position.z);
    }
    
    const newHeight = maxHeight === undefined ? 0 : maxHeight + NEW_FLOORPLAN_SPACING_IN_Z;

    // First create the floor without s3Url
    addFloor({
      name: floorName,
      position: { x: 0, y: 0, z: newHeight },
      metadata: {
        mpp: mpp,
        height: height,
        floorplan: {
          width: selectedFile.dimensions.width,
          height: selectedFile.dimensions.length
        }
      }
    }, {
      onSuccess: (data: Floor | undefined) => {
        if (!data) {
          toast.error("Failed to create floor");
          return;
        }
        setCreatedFloorId(data.id);
        
        // Update the uppy file with floor ID as filename
        const fileExtension = selectedFile.s3Name.split('.').pop() || 'png';
        const newFileName = `${data.id}.${fileExtension}`;
        
        // Update the existing file in uppy
        const files = uppy.getFiles();
        if (files.length > 0) {
          const file = files[0];
          uppy.setFileMeta(file.id, {
            ...file.meta,
            s3Key: `projects/${project?.id}/floorplans/${newFileName}`
          });
          uppy.setFileState(file.id, {
            name: newFileName
          });
        }
        
        setIsCreating(false);
        // Start upload immediately after floor creation
        uppy.upload();
      },
      onError: () => {
        setIsCreating(false);
        toast.error("Failed to create floor");
      }
    });
  }

  return (
    <Modal
      open={open}
      onClose={onCloseModal}
    >
      <Stack
        gap={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "6px",
          backgroundColor: "#222",
          minWidth: "35rem",
          padding: "1.5rem",
          maxHeight: "90vh",
          overflowY: "auto"
        }}>
        <Stack>
          <Typography variant="h2">New Floor</Typography>
        </Stack>
        <Stack gap={2}>
          <TextField label="Floor Name" variant="outlined" value={floorName} onChange={(e) => setFloorName(e.target.value)} />
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
  <TextField 
    label="MPP (Meters Per Pixel)" 
    type="number" 
    variant="outlined" 
    fullWidth 
    margin="normal"
    onChange={(e) => setMpp(parseFloat(e.target.value))}
  />
  <TextField 
    label="Height (Meters)" 
    type="number" 
    variant="outlined" 
    fullWidth 
    margin="normal"
    onChange={(e) => setHeight(parseFloat(e.target.value))}
  />

          </Stack>
          <Dashboard 
            uppy={uppy}
            proudlyDisplayPoweredByUppy={false}
            hideUploadButton={true}
            theme="dark"
            style={{
              border: "#888 solid 2px",
              borderRadius: "6px",
            }}
          />
        </Stack>
        <Stack>
          <Button variant="contained" onClick={createFloor} disabled={createDisabled}>
            {isCreating || isUploading || isUpdatingFloorImage ? "Uploading Floor..." : "Create Floor"}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  )
}

export default AddFloorModal